'use client'

import type { ComponentType } from 'react'

import dynamic from 'next/dynamic'

import type { PopupKind } from '@/lib/popups/constants'

import { CaptionCallPopup } from './captioncall-popup'
import { CardPopup } from './card-popup'
import { GeneralPopup } from './general-popup'
import { ImagePopup } from './image-popup'
import { PopupTemplateLoading } from './template-loading'
import type { PopupTemplateProps } from './template-props'

// This registry is also reachable through the server's Storyblok component
// map via popup_inline. Split interactive flows here, in the client boundary:
// a server-side import() still puts every template in the article entry chunk.
// Keep SSR on for authored inline embeds; the local loading boundary covers
// client navigation while the selected template's module is fetched.
const OtcQuizPopup = dynamic(() => import('./otc-quiz/otc-quiz-popup').then((m) => m.OtcQuizPopup), {
  loading: PopupTemplateLoading,
})
const SurveyPopup = dynamic(() => import('./survey/survey-popup').then((m) => m.SurveyPopup), {
  loading: PopupTemplateLoading,
})
const HearingGuidePopup = dynamic(
  () => import('./hearing-guide/hearing-guide-popup').then((m) => m.HearingGuidePopup),
  {
    loading: PopupTemplateLoading,
  }
)

// Dynamic on purpose: this module is in every page's shell chunk (SitePopups),
// but suggest-app is an inline-first template that most pages never render —
// the chunk loads only when a popup_inline blok (or a triggered popup) shows it.
const SuggestAppPopup = dynamic(() => import('./suggest-app/suggest-app-popup').then((m) => m.SuggestAppPopup))

// kind + template id → renderer. A custom one-off design is a new component
// here plus its id in POPUP_TEMPLATES (lib/popups/constants.ts) so the admin
// template select offers it. Unknown ids fall back to the kind's first entry
// so a stale DB value can't blank a popup.
const TEMPLATES: Record<PopupKind, Record<string, ComponentType<PopupTemplateProps>>> = {
  image: { default: ImagePopup },
  card: { default: CardPopup },
  modal: {
    general: GeneralPopup,
    // Compatibility during rollout: the migration changes CaptionCall to the
    // general template, but an environment that has not applied it yet must
    // continue rendering the legacy row instead of falling back to blank copy.
    captioncall: CaptionCallPopup,
    'otc-quiz': OtcQuizPopup,
    'hearing-survey': SurveyPopup,
    'suggest-app': SuggestAppPopup,
    'hearing-guide': HearingGuidePopup,
  },
}

export const resolveTemplate = (kind: PopupKind, template: string): ComponentType<PopupTemplateProps> =>
  TEMPLATES[kind][template] ?? Object.values(TEMPLATES[kind])[0]

// Panel width for kind='modal', per template. The quiz/survey flows use the
// full 720px; the hearing-guide invite is a single narrow column and reads
// tighter (and more like a chat) at 560px. Consumed by ModalFrame and the
// admin preview's ModalFrameStatic so both stay in sync.
export const modalPanelWidthClass = (template: string): string =>
  template === 'hearing-guide' ? 'max-w-[560px]' : 'max-w-[720px]'
